import { useEffect, useMemo, useState } from "react";
import MUISearchIcon from "@mui/icons-material/Search";
import MUIAvatar from "@mui/material/Avatar";
import MUIButton from "@mui/material/Button";
import MUICircularProgress from "@mui/material/CircularProgress";
import MUIDialogActions from "@mui/material/DialogActions";
import MUIInputAdornment from "@mui/material/InputAdornment";
import MUIList from "@mui/material/List";
import MUIListItemAvatar from "@mui/material/ListItemAvatar";
import MUIListItemButton from "@mui/material/ListItemButton";
import MUIListItemText from "@mui/material/ListItemText";
import MUIStack from "@mui/material/Stack";
import MUITextField from "@mui/material/TextField";
import MUITypography from "@mui/material/Typography";
import DialogFunctional from "../DialogFunctional";
import EmptyState from "../EmptyState";
import Format from "../../Utility/Format";
import User from "../../Utility/Crud/User";

/**
 * Renders the user picker used to transfer an inbound fax.
 * @param {object} props Component properties.
 * @param {boolean} props.open Whether the dialog is open.
 * @param {string} props.currentRecipientContact The fax's current recipient
 * contact.
 * @param {function} props.onClose Closes the dialog.
 * @param {function} props.onTransfer Transfers the fax to the selected user.
 * @param {array} props.userOptions The available user options.
 * @returns {React.ReactElement} The transfer dialog.
 */
export default function DialogInboundFileTransfer(props) {
  const [searchText, setSearchText] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!props.open) {
      setSearchText("");
      setSelectedUser(null);
    }
  }, [props.open]);

  const filteredUserOptions = useMemo(() => {
    const normalizedSearchText = searchText.trim().toLowerCase();
    const normalizedPhoneSearchText = Format.phone(searchText).replace(
      /\D/g,
      "",
    );
    const currentRecipientPhoneSearchText = Format.phone(
      props.currentRecipientContact,
    ).replace(/\D/g, "");
    const transferableUserOptions = props.userOptions.filter((userOption) => {
      return userOption.phoneSearchText !== currentRecipientPhoneSearchText;
    });

    if (normalizedSearchText === "") {
      return transferableUserOptions;
    }

    return transferableUserOptions.filter((userOption) => {
      return (
        userOption.searchText.includes(normalizedSearchText) ||
        (normalizedPhoneSearchText &&
          userOption.phoneSearchText.includes(normalizedPhoneSearchText))
      );
    });
  }, [props.currentRecipientContact, props.userOptions, searchText]);

  /**
   * Handles closing the transfer dialog.
   * @returns {void}
   */
  function handleClose() {
    if (!isLoading) {
      props.onClose();
    }
  }

  /**
   * Transfers the fax to the selected user.
   * @returns {Promise<void>}
   */
  async function handleTransfer() {
    setIsLoading(true);

    try {
      const didTransfer = await props.onTransfer(selectedUser);

      if (didTransfer) {
        props.onClose();
      }
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * Renders the dialog header.
   * @returns {React.ReactElement} The dialog header.
   */
  function renderHeader() {
    return <MUITypography variant="h6">Transfer Fax</MUITypography>;
  }

  /**
   * Renders the dialog content.
   * @returns {React.ReactElement} The dialog content.
   */
  function renderContent() {
    return (
      <MUIStack spacing={2}>
        <MUITextField
          autoFocus={true}
          fullWidth={true}
          label="Search users"
          placeholder="Name, fax number, or location"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          disabled={isLoading}
          InputProps={{
            startAdornment: (
              <MUIInputAdornment position="start">
                <MUISearchIcon />
              </MUIInputAdornment>
            ),
          }}
        />
        {filteredUserOptions.length > 0 ? (
          <MUIList
            disablePadding={true}
            sx={{ maxHeight: 400, overflow: "auto" }}
          >
            {filteredUserOptions.map((userOption) => {
              return (
                <MUIListItemButton
                  key={userOption.user.id}
                  selected={selectedUser?.id === userOption.user.id}
                  onClick={() => setSelectedUser(userOption.user)}
                  disabled={isLoading}
                  sx={{ borderRadius: 1 }}
                >
                  <MUIListItemAvatar>
                    <MUIAvatar>
                      {User.getDisplayName(userOption.user).charAt(0)}
                    </MUIAvatar>
                  </MUIListItemAvatar>
                  <MUIListItemText
                    primary={User.getDisplayName(userOption.user)}
                    secondary={[
                      Format.phone(userOption.user.fax),
                      userOption.locationNames.join(", "),
                    ]
                      .filter(Boolean)
                      .join(" • ")}
                  />
                </MUIListItemButton>
              );
            })}
          </MUIList>
        ) : (
          <EmptyState
            line1="No users match your search."
            line2="Try a different name, fax number, or location."
          />
        )}
      </MUIStack>
    );
  }

  /**
   * Renders the dialog actions.
   * @returns {React.ReactElement} The dialog actions.
   */
  function renderActions() {
    return (
      <MUIDialogActions sx={{ px: 3, py: 2 }}>
        <MUIButton
          variant="contained"
          onClick={handleTransfer}
          disabled={!selectedUser || isLoading}
          startIcon={
            isLoading ? <MUICircularProgress size={18} color="inherit" /> : null
          }
        >
          Transfer
        </MUIButton>
      </MUIDialogActions>
    );
  }

  /**
   * Gets the dialog maximum width.
   * @returns {string} The dialog maximum width.
   */
  function getMaxWidth() {
    return "sm";
  }

  /**
   * Gets whether the dialog content has dividers.
   * @returns {boolean} Whether the dialog content has dividers.
   */
  function getDividers() {
    return true;
  }

  /**
   * Gets the dialog content styles.
   * @returns {object} The dialog content styles.
   */
  function getContentSx() {
    return { borderTop: "none", borderBottom: "none" };
  }

  return (
    <DialogFunctional
      open={props.open}
      disableEscapeKeyDown={isLoading}
      onClose={handleClose}
      renderHeader={renderHeader}
      renderContent={renderContent}
      renderActions={renderActions}
      getMaxWidth={getMaxWidth}
      getDividers={getDividers}
      getContentSx={getContentSx}
    />
  );
}
